import { formatPrice } from '@affine/core/utils/format-price';

export interface PurchaseOrderSummaryItem {
  label: string;
  value: string;
  emphasized?: boolean;
}

export interface PurchaseOrderSummary {
  productName: string;
  items: PurchaseOrderSummaryItem[];
}

type SubscriptionInterval = 'monthly' | 'yearly';
type SummaryLanguage = 'de' | 'en';
type PurchaseConsentMode = 'service' | 'digital_content';

const copy = {
  de: {
    billing: 'Abrechnung',
    cancellation:
      'Jederzeit kündbar mit Wirkung zum Ende des laufenden Abrechnungszeitraums.',
    cancellationLabel: 'Kündigung',
    immediateAccess:
      'Sofortiger Leistungsbeginn. Widerruf und Wertersatz richten sich nach der Widerrufsbelehrung.',
    immediateDigitalContent:
      'Sofortige Bereitstellung der Credits. Das Widerrufsrecht erlischt mit Beginn der Bereitstellung.',
    minimumTerm: 'Mindestlaufzeit',
    monthly: 'monatlich',
    monthlyCosts: 'Monatliche Gesamtkosten',
    oneMonth: '1 Monat',
    price: 'Gesamtpreis',
    renewalMonthly: 'Automatische Verlängerung um jeweils 1 Monat.',
    renewalYearly: 'Automatische Verlängerung um jeweils 12 Monate.',
    term: 'Laufzeit',
    withdrawalLabel: 'Widerruf',
    yearly: 'jährlich',
    yearlyTerm: '12 Monate',
  },
  en: {
    billing: 'Billing',
    cancellation:
      'Cancelable any time with effect at the end of the current billing period.',
    cancellationLabel: 'Cancellation',
    immediateAccess:
      'Immediate access starts now. Withdrawal and compensation follow the withdrawal notice.',
    immediateDigitalContent:
      'Credits are provided immediately. The withdrawal right expires when provision begins.',
    minimumTerm: 'Minimum term',
    monthly: 'monthly',
    monthlyCosts: 'Monthly total',
    oneMonth: '1 month',
    price: 'Total price',
    renewalMonthly: 'Automatically renews for 1 month at a time.',
    renewalYearly: 'Automatically renews for 12 months at a time.',
    term: 'Term',
    withdrawalLabel: 'Withdrawal',
    yearly: 'yearly',
    yearlyTerm: '12 months',
  },
} as const;

export function buildSubscriptionOrderSummary(params: {
  amountCents: number;
  currency: string;
  interval: SubscriptionInterval;
  language: SummaryLanguage;
  productName: string;
}): PurchaseOrderSummary {
  const texts = copy[params.language];
  const locale = params.language === 'en' ? 'en-US' : 'de-DE';
  const intervalLabel =
    params.interval === 'yearly' ? texts.yearly : texts.monthly;
  const term = params.interval === 'yearly' ? texts.yearlyTerm : texts.oneMonth;
  const renewal =
    params.interval === 'yearly' ? texts.renewalYearly : texts.renewalMonthly;
  const monthlyTotal =
    params.interval === 'yearly'
      ? formatPrice(
          Math.round(params.amountCents / 12),
          params.currency,
          locale
        )
      : formatPrice(params.amountCents, params.currency, locale);
  const totalPrice = formatPrice(params.amountCents, params.currency, locale);

  return {
    productName: params.productName,
    items: [
      {
        label: texts.price,
        value: `${totalPrice} / ${intervalLabel}`,
        emphasized: true,
      },
      { label: texts.billing, value: intervalLabel },
      { label: texts.monthlyCosts, value: monthlyTotal },
      { label: texts.minimumTerm, value: term },
      { label: texts.term, value: renewal },
      { label: texts.cancellationLabel, value: texts.cancellation },
      { label: texts.withdrawalLabel, value: texts.immediateAccess },
    ],
  };
}

export function buildLabeledPurchaseOrderSummary(params: {
  consentMode?: PurchaseConsentMode;
  isSubscription: boolean;
  language: SummaryLanguage;
  productName: string;
  totalPriceLabel: string;
}): PurchaseOrderSummary {
  const texts = copy[params.language];
  const items: PurchaseOrderSummaryItem[] = [
    {
      label: texts.price,
      value: params.totalPriceLabel,
      emphasized: true,
    },
  ];

  if (params.isSubscription) {
    items.push(
      {
        label: texts.billing,
        value: params.totalPriceLabel,
      },
      {
        label: texts.cancellationLabel,
        value: texts.cancellation,
      }
    );
  }

  items.push({
    label: texts.withdrawalLabel,
    value:
      params.consentMode === 'digital_content'
        ? texts.immediateDigitalContent
        : texts.immediateAccess,
  });

  return {
    productName: params.productName,
    items,
  };
}
